// QueuePositionSection.jsx
import React, { useState, useEffect } from 'react';
import { PredictionQueue } from '../../../../cabinet/Application/components/prediction/prediction';
import { getKgInfo } from '../../../../cabinet/wizplain/activity';

// Статуси при яких заявка вже не стоїть в черзі
const INACTIVE_STATUSES = {
  'child-in-kindergarten': 'Дитина вже оформлена в садок',
  'invite-accepted':       'Запрошення прийняте — дитина очікує оформлення',
  'declined':              'Заявка відхилена',
  'banned':                'Заявка не обслуговується',
  'removed':               'Заявка видалена',
};

// ---------------------------------------------------------------------------
// Адаптер: applicationData (mapped) → формат application для PredictionQueue
// ---------------------------------------------------------------------------
const toApplicationFormat = (applicationData) => ({
  queueNumber:    applicationData.queueNumber,
  preferDateFrom: applicationData.desiredDate,
  youngerGroup:   applicationData.ageMenu,
  child: {
    birthDate: applicationData.birthDate,
  },
  specialization: applicationData.diseaseType
    ? { specializationId: applicationData.diseaseType }
    : null,
});

// ---------------------------------------------------------------------------
// QueuePositionSection
// ---------------------------------------------------------------------------
const QueuePositionSection = ({ applicationData }) => {
  const kindergartens = applicationData.selectedSchools || [];

  const [selectedKgId, setSelectedKgId] = useState(
    kindergartens[0] || null
  );
  const [kgInfoMap, setKgInfoMap] = useState({}); // { [_id]: { name, number } }

  // Завантажуємо назви всіх обраних садків
  useEffect(() => {
    if (!kindergartens.length) return;
    const load = async () => {
      const entries = await Promise.all(
        kindergartens.map(async (kgId) => {
          try {
            const result = await getKgInfo(kgId);
            const kg = result?.[0];
            return [kgId, kg ? { name: kg.name, number: kg.number } : null];
          } catch {
            return [kgId, null];
          }
        })
      );
      setKgInfoMap(Object.fromEntries(entries));
    };
    load();
  }, [kindergartens.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const kgLabel = (kgId) => {
    const info = kgInfoMap[kgId];
    if (!info) return kgId; // поки завантажується — показуємо id
    return `№${info.number} ${info.name}`;
  };

  const currentStatusName = applicationData.currentStatus?.name ?? applicationData.currentStatus;
  const inactiveReason = INACTIVE_STATUSES[currentStatusName];

  if (inactiveReason) {
    return (
      <div className="queue-position-section">
        <h3 className="section-title">Позиція в черзі</h3>
        <div className="alert alert-secondary">
          ℹ️ {inactiveReason} — позиція в черзі недоступна
        </div>
      </div>
    );
  }

  if (!kindergartens.length) {
    return (
      <div className="queue-position-section">
        <h3 className="section-title">Позиція в черзі</h3>
        <div className="alert alert-warning">Садки не обрані у заявці</div>
      </div>
    );
  }

  if (!applicationData.queueNumber) {
    return (
      <div className="queue-position-section">
        <h3 className="section-title">Позиція в черзі</h3>
        <div className="alert alert-warning">Заявка не має номеру черги</div>
      </div>
    );
  }

  const application = toApplicationFormat(applicationData);

  return (
    <div className="queue-position-section">
      <h3 className="section-title">Позиція в черзі</h3>

      {/* Назва поточного садка — завжди видима */}
      <div className="data-row mb-3">
        <span className="detail-label">Заклад дошкільної освіти:</span>
        {kindergartens.length === 1 ? (
          <span className="detail-value ms-2">{kgLabel(selectedKgId)}</span>
        ) : (
          <select
            className="form-select mt-1"
            value={selectedKgId}
            onChange={(e) => setSelectedKgId(e.target.value)}
          >
            {kindergartens.map((kgId) => (
              <option key={kgId} value={kgId}>
                {kgLabel(kgId)}
              </option>
            ))}
          </select>
        )}
      </div>

      {selectedKgId && (
        <PredictionQueue
          key={selectedKgId}
          kg={{ _id: selectedKgId }}
          application={application}
        />
      )}
    </div>
  );
};

export default QueuePositionSection;